// src/slices/jobs/thunk.ts
import { createAsyncThunk } from "@reduxjs/toolkit";
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/storage';
import { setJobs, setLoading, setError } from "./reducer";
import { Job } from '../../models/Job.interface';

import image1 from '../../assets/images/samplejob/001.jpg';
import image2 from '../../assets/images/samplejob/002.jpg';
import image3 from '../../assets/images/samplejob/003.jpg';
import image4 from '../../assets/images/samplejob/004.jpg';
import image5 from '../../assets/images/samplejob/005.jpg';
import image6 from '../../assets/images/samplejob/006.jpg';

// Helper function to serialize dates to ISO strings
// Helper function to serialize dates to ISO strings
const serializeDate = (job: any): Job => {
  // Convert Date objects to ISO strings
  let dtCreated: string;
  
  if (typeof job.dtCreated === 'string') {
    dtCreated = job.dtCreated;
  } else if (job.dtCreated instanceof Date) {
    dtCreated = job.dtCreated.toISOString();
  } else if (job.dtCreated && typeof job.dtCreated.toDate === 'function') {
    // Handle Firestore Timestamp
    dtCreated = job.dtCreated.toDate().toISOString();
  } else {
    // Default to current date if no valid date is provided
    dtCreated = new Date().toISOString();
  }
  
  return {
    ...job,
    dtCreated
  };
};

// This function creates a static "Add Job" card
const createAddJobCard = (): Job => {
  return {
    id: 'add-job-card',
    jobTitle: 'Start Your Next Home Improvement or Renovation Project Today!',
    jobDescription: 'Create a new job listing to get started with your project',
    jobDetail: 'Ready to enhance your home? Create a new job listing to get bids from skilled contractors and bring your vision to life.',
    jobCategories: [],
    propertyAddress: {
      address1: "123 Main St",
      city: "Virginia Beach",
      state: "VA",
      zipCode: "23454"
    },
    jobSummary: 'Ready to enhance your home? Create a new job listing to get bids from skilled contractors and bring your vision to life.',
    complete: false,
    dtCreated: new Date().toISOString(), // Use ISO string instead of Date object
    jobStatus: 'Ready',
    userId: '',
    jobAttachments: [
      { id: 'sample-0', url: image1, caption: '', order: 0 },
      { id: 'sample-1', url: image2, caption: '', order: 1 },
      { id: 'sample-2', url: image3, caption: '', order: 2 },
      { id: 'sample-3', url: image4, caption: '', order: 3 },
      { id: 'sample-4', url: image5, caption: '', order: 4 },
      { id: 'sample-5', url: image6, caption: '', order: 5 }
    ]
  };
};

// In src/slices/jobs/thunk.ts
// Update the fetchJobs function

export const fetchJobs = createAsyncThunk(
  'jobs/fetchJobs',
  async (userId: string, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      
      const firestore = firebase.firestore();
      const jobsQuery = firestore.collection('jobs')
        .where('userId', '==', userId)
        .orderBy('dtCreated', 'desc');
      
      const jobsSnapshot = await jobsQuery.get();
      
      const jobsData = jobsSnapshot.docs.map(doc => {
        const data = doc.data();
        // Convert Firestore timestamp to ISO string
        const dtCreated = data.dtCreated instanceof firebase.firestore.Timestamp 
          ? data.dtCreated.toDate().toISOString()
          : data.dtCreated;
        
        // Ensure attachments have proper structure (id, url, caption, order)
        const normalizedAttachments = Array.isArray(data.jobAttachments) 
          ? data.jobAttachments.map(attachment => {
              // Check if attachment is a valid object with required properties
              if (attachment && typeof attachment === 'object' && typeof attachment.url === 'string') {
                return {
                  id: attachment.id || `attachment-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
                  url: attachment.url,
                  caption: attachment.caption || '',
                  order: typeof attachment.order === 'number' ? attachment.order : 0
                };
              }
              return null;
            }).filter(Boolean) // Remove any null items
          : [];
          
        console.log(`Job ${doc.id} attachments:`, normalizedAttachments);
        
        return {
          id: doc.id,
          ...data,
          dtCreated,
          jobAttachments: normalizedAttachments
        };
      });

      // Add the "Create Job" card
      const allJobs = [...jobsData, createAddJobCard()];
      
      dispatch(setJobs(allJobs as Job[]));
    } catch (error) {
      console.error('Error fetching jobs:', error);
      dispatch(setError(error instanceof Error ? error.message : 'Unknown error'));
    }
  }
);

export const createJob = createAsyncThunk(
  'jobs/createJob',
  async (jobData: Partial<Job>, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      
      const firestore = firebase.firestore();
      const newJobRef = firestore.collection('jobs').doc();
      
      // Create a new Date and convert it to ISO string
      const createdDate = new Date().toISOString();
      
      // Create a serialized job object with all required fields
      const jobToSave = {
        ...jobData,
        dtCreated: createdDate,
        // Ensure all required fields have default values
        jobTitle: jobData.jobTitle || '',
        jobDescription: jobData.jobDescription || '',
        jobDetail: jobData.jobDetail || '',
        jobCategories: jobData.jobCategories || [],
        propertyAddress: jobData.propertyAddress || { address1: '', city: '', state: '', zipCode: '' },
        jobSummary: jobData.jobSummary || '',
        complete: jobData.complete || false,
        jobStatus: jobData.jobStatus || 'Draft',
        userId: jobData.userId || ''
      };
      
      await newJobRef.set(jobToSave);
      
      // Refetch all jobs to update the state
      if (jobData.userId) {
        dispatch(fetchJobs(jobData.userId));
      }
      
      return newJobRef.id;
    } catch (error) {
      console.error('Error creating job:', error);
      dispatch(setError(error instanceof Error ? error.message : 'Unknown error'));
      throw error;
    }
  }
);

export const updateJob = createAsyncThunk(
  'jobs/updateJob',
  async (jobData: Job, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      
      const firestore = firebase.firestore();
      const jobRef = firestore.collection('jobs').doc(jobData.id);
      
      // Serialize the job data to ensure dates are ISO strings
      const serializedJobData = serializeDate(jobData);
      
      // Remove id from the data to update
      const { id, ...updateData } = serializedJobData;
      
      await jobRef.update(updateData);
      
      // Refetch all jobs to update the state
      if (jobData.userId) {
        dispatch(fetchJobs(jobData.userId));
      }
      
      return jobData.id;
    } catch (error) {
      console.error('Error updating job:', error);
      dispatch(setError(error instanceof Error ? error.message : 'Unknown error'));
      throw error;
    }
  }
);

export const deleteJob = createAsyncThunk(
  'jobs/deleteJob',
  async ({ jobId, userId }: { jobId: string, userId: string }, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      
      const firestore = firebase.firestore();
      const storage = firebase.storage();
      
      // Delete job document
      await firestore.collection('jobs').doc(jobId).delete();
      
      // Delete associated storage files
      try {
        const jobStorageRef = storage.ref(`jobs/${jobId}`);
        const filesList = await jobStorageRef.listAll();
        
        // Delete all files in the job folder
        await Promise.all(filesList.items.map(fileRef => fileRef.delete()));
      } catch (storageError) {
        console.warn('Error deleting job files:', storageError);
        // Continue even if file deletion fails
      }
      
      // Refetch jobs to update state
      dispatch(fetchJobs(userId));
      
      return jobId;
    } catch (error) {
      console.error('Error deleting job:', error);
      dispatch(setError(error instanceof Error ? error.message : 'Unknown error'));
      throw error;
    }
  }
);